import { formatCurrency } from "@angular/common";
import { HttpErrorResponse } from "@angular/common/http";
import { Component, inject, Input, LOCALE_ID, OnDestroy, OnInit, signal, ViewChild } from "@angular/core";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { ChartJsContainerComponent } from "@tylertech/chart-js";
import { ChartDialogService, ChartManagerService, ChartRegistryService, ChartService } from "@tylertech/charting-module";
import { Command, CommandBarComponent, IconCommand } from "@tylertech/commands-module";
import { BusyIndicatorComponent, formatAbbreviatedNumber, TruncateTextComponent } from "@tylertech/common-components-module";
import { FileService, LoggingService, NavigationService, UrlService, } from "@tylertech/core-module";
import { ForgeCardModule, ForgeToolbarModule } from "@tylertech/forge-angular";
import { IntentService } from "@tylertech/intents-module";
import { AppLinkParms, ExecutionResult, FilterItem, IChartCriteria, } from "@tylertech/tyler-api-types";
import { ActiveElement, Chart, ChartConfiguration, ChartEvent, TooltipItem } from "chart.js";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "purchasing-requisition-approval-chart",
    templateUrl: "./requisitionApprovalChart.tmpl.html",
    imports: [
        BusyIndicatorComponent,
        ChartJsContainerComponent,
        CommandBarComponent,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        ForgeCardModule,
        ForgeToolbarModule,
        TruncateTextComponent,
    ]
})
export class RequisitionApprovalChartComponent implements OnDestroy, OnInit
{
    private readonly locale = inject(LOCALE_ID);
    private readonly $chartRegistryService = inject(ChartRegistryService);
    private readonly $chartService = inject(ChartService);
    private readonly $loggingService = inject(LoggingService);
    private readonly $chartDialogService = inject(ChartDialogService);
    private readonly $fileService = inject(FileService);
    private readonly $urlService = inject(UrlService);
    private readonly $navigationService = inject(NavigationService);
    private readonly $intentService = inject(IntentService);

    @Input("chartId") private readonly _chartId: string;

    @ViewChild(ChartJsContainerComponent) private readonly chartContainer: ChartJsContainerComponent;

    private readonly destructor$ = new Subject<void>();
    private _chartCriteria: IChartCriteria;

    readonly commands: ReadonlyArray<Command>;
    readonly showChart = signal(false);
    chartConfig: ChartConfiguration<"bar">;

    ChartTitle: string = "Open Requisitions by Requestor";
    ChartSubtitle: string;
    isLoaded: boolean;
    total: string;

    constructor()
    {
        const $chartManagerService = inject(ChartManagerService);

        $chartManagerService.refreshChartObservable.pipe(takeUntil(this.destructor$)).subscribe((chartId: string) =>
        {
            if (chartId !== this.chartId)
            {
                return;
            }
            this.loadChartData();
        });
        this.commands = [
            new IconCommand({
                icon: "share",
                label: null,
                action: () => this.shareChart(),
                getEnabled: () => this.hasData(),
                tooltip: `Share ${this.ChartTitle}`,
            }),
        ];
    }

    get chartId(): string | undefined
    {
        return this._chartId || this.chartCriteria?.ChartId;
    }

    @Input()
    get chartCriteria(): IChartCriteria | undefined
    {
        return this._chartCriteria;
    }

    set chartCriteria(value: IChartCriteria)
    {
        this._chartCriteria = value;
    }

    get chartHasData(): boolean
    {
        return this.hasData();
    }

    get errorMessage(): string | undefined
    {
        return this.chartCriteria?.ErrorMessage;
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        new Promise((_resolveFn, rejectFn) =>
        {
            if (!this.hasData())
            {
                return this.loadChartData();
            }
            try
            {
                this.updateChartConfiguration();
            }
            catch (err)
            {
                rejectFn(err);
            }
        })
            .catch(e =>
            {
                console.error("Error on load", e);
            })
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    hasData(): boolean
    {
        return this.chartCriteria?.ChartDatas?.length > 0;
    }

    private loadChartData(): Promise<void>
    {
        this.isLoaded = false;
        return this.$chartRegistryService.GetChartData(this.chartCriteria)
            .then(
                (result: IChartCriteria) =>
                {
                    this.chartCriteria = result;
                },
                (error: HttpErrorResponse) =>
                {
                    this.$loggingService.error("Could not retrieve chart data", error);
                    this.chartCriteria = {
                        ErrorMessage: error.error.Message,
                    } as IChartCriteria;
                }
            )
            .then(
                () =>
                {
                    if (!this.hasData())
                    {
                        return;
                    }
                    try
                    {
                        this.updateChartConfiguration();
                    }
                    catch (err)
                    {
                        return Promise.reject(err);
                    }
                },
                (err: unknown) =>
                {
                    console.error(err);
                }
            )
            .finally(() =>
            {
                this.isLoaded = true;
            });
    }

    openSettings(): void
    {
        this.$chartDialogService.showChartParametersDialog(this.ChartTitle, this.chartCriteria)
            .then((criteria: IChartCriteria) =>
            {
                this.chartCriteria = criteria;
                this.loadChartData();
            });
    }

    private shareChart(): void
    {
        const img = this.chartContainer.createImage();
        this.$fileService.openDocumentLink(img, "image/png", this.ChartTitle.replace(/ /g, ""));
    }

    private updateChartConfiguration(): void
    {
        this.chartConfig = {
            type: "bar",
            options: {
                maintainAspectRatio: false,
                responsive: true,
                plugins: {
                    legend: {
                        position: "left",
                        display: false,
                    },
                    tooltip: {
                        callbacks: {
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                const dataObject = this.chartCriteria?.ChartDatas[0].Data[tooltipItem.dataIndex];
                                const label = dataObject.Tooltip;
                                const count = dataObject.Count;
                                const value = dataObject.Amount;
                                return `${label} ( Count: ${count}; Total: ${formatCurrency(value, this.locale, "$")} )`;
                            },
                        },
                    },
                },
                scales: {
                    y: {
                        type: "linear",
                        display: true,
                        grid: { display: true },
                        ticks: {
                            callback: (value: number) =>
                            {
                                return formatAbbreviatedNumber(value > -10 && value < 10 ? value.toFixed(2) : value.toFixed(0));
                            },
                        },
                    }
                },
                // TODO(DO-7754) - Can some of this be standardize and pulled out somewhere?
                onClick: (event: ChartEvent & Event, _elements: ActiveElement[], chart: Chart): void =>
                {
                    const elements = chart.getElementsAtEventForMode(event, 'nearest', { intersect: true }, true);
                    const element = elements[0];
                    if (!element)
                    {
                        return null;
                    }
                    const index = element.index;
                    const filter: FilterItem[] = [this.chartCriteria.ChartDatas[0].Data[index].FilterParam];
                    const additionalFilters = this.chartCriteria.ChartDatas[0].Additional || [];
                    if (additionalFilters.length > 0)
                    {
                        this.chartCriteria.ChartDatas[0].Additional.forEach(g => filter.push(g));
                    }
                    const parms: AppLinkParms = {
                        App: "requisitions",
                        Parms: filter,
                    };
                    this.$urlService.GetAppLink(parms).then((result: ExecutionResult<string>) =>
                    {
                        const appLink = result.Result;
                        const index = appLink.indexOf("?");
                        if (appLink.includes("apps/form"))
                        {
                            this.$navigationService.navigateToApp(appLink);
                        }
                        else if (index > 0)
                        {
                            this.$intentService.resolve("Requisitions", "Search", {
                                querystring: appLink.substring(index, appLink.length),
                            });
                        }
                    });
                },
            },
            data: {
                labels: this.chartCriteria.ChartDatas[0].Labels,
                datasets: [
                    {
                        type: "bar",
                        data: this.chartCriteria.ChartDatas[0].Data.map(x => x.Amount),
                        backgroundColor: this.$chartService.getColors(this.chartCriteria.ChartDatas[0].Data.length),
                    },
                ],
            },
        };

        this.showChart.set(this.chartCriteria?.ChartDatas?.length > 0);
    }
}
